import React, { useEffect, useRef, useState } from "react";
import { X, File } from "lucide-react";
import CustomPopover from "../../../../components/popover/Popover";
import IconInput from "../../../../components/controls/input/iconInput/IconInput";
import Button from "../../../../components/controls/button/Button";
import { ArrowDownMedium, Cross, SearchIcon } from "../../../../modules/default-svg";
import clientsService from "../../../../store/services/clients.service";
import portfolioService from "../../../../store/services/portfolio.service";
import industryService from "../../../../store/services/industry.service";
import { apiCall } from "../../../../store/services/service";
import { URL_SEARCH_CLIENT_PORTFOLIO } from "../../../../store/services/URL";
import styles from "../../styles/portfolioManagerDashboard.module.scss";

// "summary" (role-based dashboard, no portfolio scoped yet) can group by any dimension including
// Client/Portfolio. "detail" (Portfolio Overview "Cockpit" tab, or a portfolio picked below) is
// already scoped to one portfolio - grouping by Client or Portfolio there would always yield
// exactly one group, so those two are left out.
const GROUP_BY_OPTIONS_SUMMARY = [
  { label: "None", value: "None" },
  { label: "Client", value: "Client" },
  { label: "Portfolio", value: "Portfolio" },
  { label: "Program", value: "Program" },
  { label: "Industry", value: "Industry" },
  { label: "Project", value: "Project" },
];
const GROUP_BY_OPTIONS_DETAIL = [
  { label: "None", value: "None" },
  { label: "Program", value: "Program" },
  { label: "Industry", value: "Industry" },
  { label: "Project", value: "Project" },
];

// mode: "summary" shows the Client tab; "detail" (Portfolio Overview "Cockpit" tab, or a portfolio
// picked below) hides it - a portfolio already belongs to exactly one client, so filtering by
// client there is redundant. Group By is available in both modes (see GROUP_BY_OPTIONS_* above).
//
// portfolioId/portfolioName/onPortfolioChange are optional - only the role-based header dashboard
// passes them (it owns which single portfolio, if any, is being drilled into). When provided, a
// Portfolio tab renders regardless of mode, so the user can switch/clear it even while scoped.
const DashboardFilterBar = ({ mode = "summary", onApply, portfolioId, portfolioName, onPortfolioChange }) => {
  const [entityPopoverOpen, setEntityPopoverOpen] = useState(false);
  const [entityType, setEntityType] = useState(mode === "summary" ? "client" : "industry");
  const [entitySearch, setEntitySearch] = useState("");
  const [entityItems, setEntityItems] = useState([]);
  const [entitySearching, setEntitySearching] = useState(false);

  // If the Client tab disappears (mode flips to "detail" while it was selected), fall back to a
  // tab that's actually rendered instead of leaving the search box pointed at a hidden one.
  useEffect(() => {
    if (mode !== "summary" && entityType === "client") {
      setEntityType("industry");
    }
  }, [mode, entityType]);

  const [groupByPopoverOpen, setGroupByPopoverOpen] = useState(false);

  const [client, setClient] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [groupBy, setGroupBy] = useState("None");

  // Tracks which client the current industryOptions are scoped to ("all" = unscoped/full list) -
  // so a client/portfolio switch re-fetches, but re-opening the popover doesn't re-fetch for
  // nothing.
  const industryScopeRef = useRef(undefined);

  const fetchIndustriesForScope = async (scopeClientId) => {
    const scopeKey = scopeClientId ?? "all";
    if (industryScopeRef.current === scopeKey) return;
    industryScopeRef.current = scopeKey;
    try {
      const res = scopeClientId
        ? await clientsService.getClientIndustriesRequest(scopeClientId)
        : await industryService.getIndustriesRequest();
      setIndustryOptions((res || []).map((o) => ({ label: o.name, value: o.id })));
    } catch {
      industryScopeRef.current = undefined;
      setIndustryOptions([]);
    }
  };

  // Industry search is scoped to whichever Client/Portfolio is currently selected (a portfolio's
  // own client is fetched once to resolve the scope) - falls back to the full industry list when
  // neither is set.
  useEffect(() => {
    if (client?.id) {
      fetchIndustriesForScope(client.id);
    } else if (portfolioId) {
      portfolioService
        .getPortfolioDetails({ id: portfolioId })
        .then((detail) => fetchIndustriesForScope(detail?.client?.id))
        .catch(() => fetchIndustriesForScope(undefined));
    } else {
      fetchIndustriesForScope(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id, portfolioId]);

  // Same tabs+search+pill pattern as ReportFilterBar.jsx (resourcesReport) - fetch on every
  // keystroke, guard against a stale response landing after a newer one via `cancelled`.
  // Industry skips this entirely - it's a small, already-fetched/cached list filtered client-side
  // below instead of hitting the network per keystroke.
  useEffect(() => {
    if (!entityPopoverOpen || entityType === "industry") return;
    let cancelled = false;
    setEntitySearching(true);
    const run = async () => {
      try {
        let res = [];
        if (entityType === "client") {
          res = await clientsService.searchClientsRequest(entitySearch || "");
        } else {
          const queryParam = client?.id
            ? `?clientId=${client.id}&searchTerm=${encodeURIComponent(entitySearch || "")}`
            : `?searchTerm=${encodeURIComponent(entitySearch || "")}`;
          res = await apiCall.get({ url: `${URL_SEARCH_CLIENT_PORTFOLIO}${queryParam}`, isAuthToken: true });
        }
        if (!cancelled) {
          setEntityItems(res || []);
          setEntitySearching(false);
        }
      } catch {
        if (!cancelled) {
          setEntityItems([]);
          setEntitySearching(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitySearch, entityPopoverOpen, entityType]);

  const handleSelectClient = (item) => {
    setClient({ id: item.id, name: item.name });
    setIndustries([]);
    setEntityPopoverOpen(false);
  };

  const handleSelectPortfolio = (item) => {
    // Picking a portfolio switches the whole dashboard into single-portfolio "detail" mode - the
    // Client tab (and its selection) no longer applies there, so drop it to avoid it silently
    // reappearing if the user later clears back to summary.
    setClient(null);
    setIndustries([]);
    onPortfolioChange?.({ id: item.id, name: item.name });
    setEntityPopoverOpen(false);
  };

  const toggleIndustry = (opt) => {
    setIndustries((prev) => {
      const exists = prev.some((i) => i.id === opt.value);
      return exists ? prev.filter((i) => i.id !== opt.value) : [...prev, { id: opt.value, name: opt.label }];
    });
  };

  // Names travel alongside the ids purely for display (e.g. the report header's filters recap) -
  // the dashboard's own data requests only ever use the ids.
  const buildFilters = () => ({
    clientId: client?.id,
    clientName: client?.name,
    industryIds: industries.map((i) => i.id),
    industryNames: industries.map((i) => i.name),
    groupBy,
  });

  const handleApply = () => {
    onApply(buildFilters());
    setEntityPopoverOpen(false);
  };

  const handleClear = () => {
    setClient(null);
    setIndustries([]);
    setGroupBy("None");
    onPortfolioChange?.(null);
    // Build the cleared payload explicitly rather than reading state right after
    // the setters above - those updates haven't applied yet on this render pass.
    onApply({ groupBy: "None" });
    setEntityPopoverOpen(false);
  };

  const handleGroupBySelect = (value) => {
    setGroupBy(value);
    onApply({ ...buildFilters(), groupBy: value });
    setGroupByPopoverOpen(false);
  };

  // Each tag's remove handler overrides just its own field in the payload rather than reading
  // buildFilters() after the setState call above it - state updates aren't visible yet on this
  // render pass, same pitfall as handleClear.
  const tags = [];
  if (mode === "summary" && client) {
    tags.push({
      key: "client",
      label: `Client: ${client.name}`,
      onRemove: () => {
        setClient(null);
        setIndustries([]);
        onApply({ ...buildFilters(), clientId: undefined, clientName: undefined, industryIds: [], industryNames: [] });
      },
    });
  }
  if (onPortfolioChange && portfolioId) {
    tags.push({
      key: "portfolio",
      label: `Portfolio: ${portfolioName}`,
      onRemove: () => {
        setIndustries([]);
        onPortfolioChange(null);
      },
    });
  }
  if (industries.length > 0) {
    tags.push({
      key: "industry",
      label: `Industry: ${industries.map((i) => i.name).join(", ")}`,
      onRemove: () => {
        setIndustries([]);
        onApply({ ...buildFilters(), industryIds: [], industryNames: [] });
      },
    });
  }

  const entityTabs = [
    ...(mode === "summary" ? [{ type: "client", label: "Client" }] : []),
    ...(onPortfolioChange ? [{ type: "portfolio", label: "Portfolio" }] : []),
    { type: "industry", label: "Industry" },
  ];

  const groupByOptions = mode === "summary" ? GROUP_BY_OPTIONS_SUMMARY : GROUP_BY_OPTIONS_DETAIL;

  const ENTITY_TYPE_NOUN = { client: "clients", portfolio: "portfolios", industry: "industries" };

  const handleSelectEntityItem = (item) => {
    if (entityType === "client") handleSelectClient(item);
    else if (entityType === "portfolio") handleSelectPortfolio(item);
    else toggleIndustry({ value: item.id, label: item.name });
  };

  const visibleEntityItems =
    entityType === "industry"
      ? industryOptions
          .filter((o) => o.label.toLowerCase().includes(entitySearch.toLowerCase()))
          .map((o) => ({ id: o.value, name: o.label }))
      : entityItems.filter((item) => (entityType === "client" ? item.id !== client?.id : item.id !== portfolioId));

  return (
    <div className={styles.filterBarRow}>
      <div className={styles.filterBtnGroup}>
      <CustomPopover
        width="320px"
        status={entityPopoverOpen}
        PopSurfaceClass={styles.filterPopoverSurface}
        icon={
          <Button
            btnClass={`${styles.filterBtn} btn_combo btn_combo_white ${tags.length > 0 ? styles.activeFilterBtn : ""}`}
            label={tags.length > 0 ? `Filters (${tags.length})` : "Filter by"}
            clickHandler={() => {
              setEntityPopoverOpen((o) => !o);
              setEntitySearch("");
            }}
            rightIcon={
              <span className="d-inline-flex align-items-center">
                {tags.length > 0 && (
                  <span
                    className={styles.filterClearX}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                  >
                    <X size={14} />
                  </span>
                )}
                <ArrowDownMedium />
              </span>
            }
          />
        }
      >
        <div className={styles.filterPopoverHeader}>
          <h6>Filter by</h6>
          <div className="cursor-pointer" onClick={() => setEntityPopoverOpen(false)}>
            <Cross />
          </div>
        </div>
        <div className={styles.filterPopoverBody}>
          {entityTabs.length > 1 && (
            <div className={styles.entityTypeTabs}>
              {entityTabs.map((tab) => (
                <button
                  key={tab.type}
                  type="button"
                  className={`${styles.entityTypeTab} ${entityType === tab.type ? styles.entityTypeTabActive : ""}`}
                  onClick={() => {
                    setEntityType(tab.type);
                    setEntitySearch("");
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <IconInput
            icon={<SearchIcon />}
            placeholder={`Search ${ENTITY_TYPE_NOUN[entityType]}`}
            handleChange={setEntitySearch}
            value={entitySearch}
          />

          <div style={{ maxHeight: "160px", overflow: "auto" }}>
            {entitySearching ? (
              <div className={styles.entityEmptyState}>
                <span style={{ color: "#8b8b8b", fontSize: 12 }}>Searching…</span>
              </div>
            ) : visibleEntityItems.length === 0 ? (
              <div className={styles.entityEmptyState}>
                <File size={24} color="#342c82" strokeWidth={1.5} />
                <span>No records found</span>
              </div>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {visibleEntityItems.map((item) => (
                  <span
                    key={item.id}
                    className={`${styles.filterItem} ${
                      entityType === "industry" && industries.some((i) => i.id === item.id) ? styles.filterItemActive : ""
                    }`}
                    onClick={() => handleSelectEntityItem(item)}
                  >
                    {item.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className={styles.filterPopoverActions}>
            {tags.length > 0 && (
              <span className={styles.clearFilterLink} onClick={handleClear}>
                Clear filter
              </span>
            )}
            <Button btnClass="d_btn d_btn_primary" label="Apply" clickHandler={handleApply} />
          </div>
        </div>
      </CustomPopover>

      <CustomPopover
        width="max-content"
        status={groupByPopoverOpen}
        PopSurfaceClass={styles.filterPopoverSurface}
        icon={
          <Button
            btnClass={`${styles.filterBtn} btn_combo btn_combo_white ${groupBy !== "None" ? styles.activeFilterBtn : ""}`}
            label={groupBy !== "None" ? `Group by: ${groupBy}` : "Group by"}
            clickHandler={() => setGroupByPopoverOpen((o) => !o)}
            rightIcon={
              <span className="d-inline-flex align-items-center">
                <ArrowDownMedium />
              </span>
            }
          />
        }
      >
        <div className={styles.filterPopoverHeader}>
          <h6>Group by</h6>
          <div className="cursor-pointer" onClick={() => setGroupByPopoverOpen(false)}>
            <Cross />
          </div>
        </div>
        <div className={styles.entityTypeTabs} style={{ width: "max-content" }}>
          {groupByOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`${styles.entityTypeTab} ${groupBy === opt.value ? styles.entityTypeTabActive : ""}`}
              onClick={() => handleGroupBySelect(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </CustomPopover>
      </div>

      {tags.length > 0 && (
        <div className={styles.filterTags}>
          {tags.map((tag) => (
            <span key={tag.key} className={styles.filterTag}>
              {tag.label}
              <X size={12} className={styles.filterTagRemove} onClick={tag.onRemove} />
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardFilterBar;
